/**
 * --------------------------------------------------------------------------
 * NJ : AbstractFormBaseInput.js
 * --------------------------------------------------------------------------
 */

import { EventHandler, Manipulator, Testing } from 'nj-utils';
import AbstractFormBase from 'nj-components/AbstractFormBase';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const CLASS_NAME = {
  njFormGroup: 'nj-form-group',
  njLabel: 'nj-label',
  njLabelStatic: 'nj-label-static',
  njLabelPlaceholder: 'nj-label-placeholder',
  njLabelFloating: 'nj-label-floating',
  hasDanger: 'has-danger',
  isFilled: 'is-filled',
  isFocused: 'is-focused',
  inputGroup: 'input-group'
};

const SELECTOR = {
  njFormGroup: `.${CLASS_NAME.njFormGroup}`,
  njLabelWildcard: `label[class^='${CLASS_NAME.njLabel}'], label[class*=' ${CLASS_NAME.njLabel}']`
};

const DEFAULT_CONFIG = {
  validate: false,
  njFormGroup: {
    template: 'span',
    templateClass: `${CLASS_NAME.njFormGroup}`
  },
  label: {
    required: false,
    selectors: [
      '.form-control-label', // in the case of horizontal or inline forms, this will be marked
      ':scope > label' // usual case for text inputs, first child.  Deeper would find toggle labels so don't do that.
    ],
    className: CLASS_NAME.njLabelStatic
  },
  requiredClasses: [],
  invalidComponentMatches: [],
  convertInputSizeVariations: true
};

const FORM_CONTROL_SIZE_MARKERS = {
  'form-control-lg': 'nj-form-group-lg',
  'form-control-sm': 'nj-form-group-sm'
};

class AbstractFormBaseInput extends AbstractFormBase {
  constructor(Component, element, config, properties) {
    super(Component, element, Manipulator.extend(true, DEFAULT_CONFIG, config), properties);

    if (this.constructor === AbstractFormBaseInput) {
      throw new TypeError('Abstract class "AbstractFormBaseInput" cannot be instantiated directly');
    }

    // Enforce no overlap between components to prevent side effects
    this.rejectInvalidComponentMatches();

    // Enforce required classes for a consistent rendering
    this.rejectWithoutRequiredClasses();

    // The layout has to contain the class .nj-form-group if needed. The wrapper will not be automatically added
    this.njFormGroup = this.resolveNJFormGroup();

    if (typeof this.njFormGroup !== 'undefined' && this.njFormGroup !== null) {
      // Resolve and mark the njLabel if necessary as defined by the config
      this.njLabel = this.resolveNJLabel();

      // Signal to the nj-form-group that a form-control-* variation is being used
      this.resolveNJFormGroupSizing();

      this.addFocusListener();
      this.addChangeListener();

      if (this.isEmpty()) {
        this.removeIsFilled();
      } else {
        this.addIsFilled();
      }
    }
  }

  rejectInvalidComponentMatches() {
    for (const otherComponent of this.config.invalidComponentMatches) {
      otherComponent.rejectMatch(this.constructor.name, this.element);
    }
  }

  rejectWithoutRequiredClasses() {
    let found = false;
    for (const requiredClass of this.config.requiredClasses) {
      // allow one of several classes to be passed in x||y
      if (requiredClass.indexOf('||') !== -1) {
        const oneOf = requiredClass.split('||');
        // eslint-disable-next-line
        for (const requiredClass of oneOf) {
          if (this.element.classList.contains(requiredClass)) {
            found = true;
            break;
          }
        }
      } else if (this.element.classList.contains(requiredClass)) {
        found = true;
      } else {
        found = false;
      }
    }

    return found;
  }

  addFocusListener() {
    EventHandler.on(this.element, 'focus', () => {
      this.addFormGroupFocus();
    });

    EventHandler.on(this.element, 'blur', () => {
      this.removeFormGroupFocus();
    });
  }

  addChangeListener() {
    EventHandler.on(this.element, 'keydown paste', (event) => {
      if (Testing.isChar(event)) {
        this.addIsFilled();
      }
    });
    EventHandler.on(this.element, 'keyup change', () => {
      // make sure empty is added back when there is a programmatic value change.
      //  NOTE: programmatic changing of value using $.val() must trigger the change event i.e. $.val('x').trigger('change')
      if (this.isEmpty()) {
        this.removeIsFilled();
      } else {
        this.addIsFilled();
      }

      if (this.config.validate) {
        // Validation events do not bubble, so they must be attached directly to the text: http://jsfiddle.net/PEpRM/1/
        //  Further, even the bind method is being caught, but since we are already calling #checkValidity here, just alter
        //  the form-group on change.
        //
        // NOTE: I'm not sure we should be intervening regarding validation, this seems better as a README and snippet of code.
        //        BUT, I've left it here for backwards compatibility.
        const isValid =
          typeof this.element[0].checkValidity === 'undefined' ||
          this.element[0].checkValidity();
        if (isValid) {
          this.removeHasDanger();
        } else {
          this.addHasDanger();
        }
      }
    });
  }

  addHasDanger() {
    this.njFormGroup.classList.add(CLASS_NAME.hasDanger);
  }

  removeHasDanger() {
    this.njFormGroup.classList.remove(CLASS_NAME.hasDanger);
  }

  isEmpty() {
    return (
      this.element.value === null ||
      typeof this.element.value === 'undefined' ||
      this.element.value === ''
    );
  }

  // Will add nj-form-group to form-group or create a nj-form-group if necessary
  resolveNJFormGroup() {
    return this.findFormGroup(this.config.njFormGroup.required);
  }

  // Demarcation element (e.g. first child of a form-group)
  //  Subclasses such as file inputs may have different structures
  outerElement() {
    return this.element;
  }

  // Will add nj-label to nj-form-group if not already specified
  resolveNJLabel() {
    let label = this.njFormGroup.querySelectorAll(SELECTOR.njLabelWildcard);

    if (typeof label === 'undefined' || label.length === 0) {
      // we need to find it based on the configured selectors
      label = this.findLabel(this.config.label.required);

      if (typeof label === 'undefined' || label.length === 0) {
        // no label found, and finder did not require one
      } else {
        // a candidate label was found, add the configured default class name
        label.forEach((el) => {
          el.classList.add(this.config.label.className);
        });
      }
    }

    return label;
  }

  // Find nj-label variant based on the config selectors
  findLabel(raiseError = true) {
    let label = null;

    // use the specified selector order
    for (const selector of this.config.label.selectors) {
      if (typeof selector === 'function') {
        label = selector(this);
      } else {
        label = this.njFormGroup.querySelectorAll(selector);
      }

      if (typeof label !== 'undefined' && typeof label.classList !== 'undefined' && label.length > 0) {
        break;
      }
    }

    if (label === null && raiseError) {
      console.error(
        `Failed to find ${SELECTOR.njLabelWildcard} within nj-form-group for ${Testing.describe(
          this.element
        )}`
      );
    }
    return label;
  }

  // Due to the interconnected nature of labels/inputs/help-blocks, signal the nj-form-group-* size variation based on
  //  a found form-control-* size
  resolveNJFormGroupSizing() {
    if (!this.config.convertInputSizeVariations) {
      return;
    }

    // Modification - Change text-sm/lg to form-group-sm/lg instead (preferred standard and simpler css/less variants)
    for (const inputSize in FORM_CONTROL_SIZE_MARKERS) {
      if (this.element.classList.contains(inputSize)) {
        // this.element.classList.remove(inputSize)
        this.njFormGroup.classList.add(FORM_CONTROL_SIZE_MARKERS[inputSize]);
      }
    }
  }
}

export default AbstractFormBaseInput;
